import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Redevelopment", item: "https://cashmyproperty.ai/services/redevelopment" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is FSI and why does it matter for redevelopment?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "FSI (Floor Space Index) determines how much built-up area can be constructed on your plot. Higher FSI means more saleable area. Our FSI study identifies the maximum permissible FSI under current regulations, any transferable development rights (TDR) you may be eligible for, and premium FSI options. This directly translates to higher revenue for you.",
      },
    },
    {
      "@type": "Question",
      name: "How do you get society or co-owner consent for redevelopment?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We manage the entire consent process — from initial presentations explaining the financial upside to each owner, to addressing concerns about temporary relocation, to formal consent documentation. Our team has managed consent for societies with 50+ members. We achieve 100% consent through transparent communication and fair deal structuring.",
      },
    },
    {
      "@type": "Question",
      name: "What happens to existing tenants or occupants during redevelopment?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A relocation and rent compensation plan is part of every redevelopment proposal. Tenants receive monthly rent compensation during the construction period (typically 18–24 months) and are guaranteed a unit in the new development with the same or larger area. This is structured into the developer agreement before any work begins.",
      },
    },
    {
      "@type": "Question",
      name: "How long does the entire redevelopment process take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The consent and approval phase typically takes 4–8 months depending on society size and regulatory complexity. Construction takes 18–30 months depending on project scale. Total timeline from initiation to handover is approximately 24–36 months. We provide a detailed milestone schedule during the feasibility phase.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "FSI & Feasibility Study",
    desc: "Our team conducts a detailed FSI analysis — permissible FSI, TDR eligibility, premium FSI options, and ground coverage. We model multiple development scenarios to determine the highest-revenue option for landowners.",
    icon: "ri-building-line",
  },
  {
    step: "02",
    title: "Society Consent Management",
    desc: "We present the financial proposal to all owners with transparent projections. Our consent team addresses concerns, manages objections, and secures the required majority (typically 75%) for redevelopment initiation.",
    icon: "ri-group-line",
  },
  {
    step: "03",
    title: "Developer Bidding & Selection",
    desc: "We invite bids from 3–5 pre-qualified developers with proven redevelopment track records. Each bid is scored on revenue share, construction quality, timeline commitment, and bank guarantee strength. You select the winner.",
    icon: "ri-auction-line",
  },
  {
    step: "04",
    title: "Legal & Regulatory Approvals",
    desc: "Development agreement, power of attorney, society resolutions, municipal approvals, and building plan sanctions — all handled by our legal team. We ensure full compliance with RERA and local development regulations.",
    icon: "ri-file-check-line",
  },
  {
    step: "05",
    title: "Construction Monitoring & Handover",
    desc: "Monthly progress reports, quality audits, and milestone tracking. We ensure the developer delivers on time and to specification. Upon completion, owners receive possession of their new units with enhanced area and value.",
    icon: "ri-tools-line",
  },
];

const benefits = [
  { icon: "ri-arrow-up-circle-line", title: "Unlock Hidden Land Value", desc: "Old properties sitting on prime land often have unused FSI potential worth crores. Redevelopment converts this dormant value into tangible, saleable built-up area you can monetize or occupy." },
  { icon: "ri-home-2-line", title: "New Home, Zero Cost", desc: "In a typical redevelopment, the developer bears all construction costs. Existing owners get brand-new units — often 25–40% larger — plus a corpus fund for relocation rent, all at zero out-of-pocket cost." },
  { icon: "ri-shield-star-line", title: "RERA-Registered Developers", desc: "We only work with RERA-registered developers who have completed at least 5 redevelopment projects. Bank guarantees protect your interests at every construction milestone." },
  { icon: "ri-money-rupee-circle-line", title: "Corpus Fund & Rent During Transit", desc: "Developers provide a one-time corpus fund (₹3–10 lakh per owner) plus monthly rent compensation during the 18–24 month construction period. You never pay out of pocket for temporary housing." },
  { icon: "ri-scales-3-line", title: "Full Legal Protection", desc: "Development agreements include penalty clauses for delays, bank guarantees linked to milestones, and clear handover conditions. Our legal team ensures no loopholes left for the developer to exploit." },
  { icon: "ri-building-4-line", title: "Modern Amenities & Appreciation", desc: "Post-redevelopment, the property typically appreciates 200–300%. Old walk-up buildings become modern towers with lifts, parking, gym, and security — dramatically increasing both living quality and asset value." },
];

const faqs = [
  { q: "What is FSI and why does it matter for redevelopment?", a: "FSI (Floor Space Index) determines how much built-up area can be constructed on your plot. Higher FSI means more saleable area. Our FSI study identifies the maximum permissible FSI under current regulations, any transferable development rights (TDR) you may be eligible for, and premium FSI options. This directly translates to higher revenue for you." },
  { q: "How do you get society or co-owner consent for redevelopment?", a: "We manage the entire consent process — from initial presentations explaining the financial upside to each owner, to addressing concerns about temporary relocation, to formal consent documentation. Our team has managed consent for societies with 50+ members. We achieve 100% consent through transparent communication and fair deal structuring." },
  { q: "What happens to existing tenants or occupants during redevelopment?", a: "A relocation and rent compensation plan is part of every redevelopment proposal. Tenants receive monthly rent compensation during the construction period (typically 18–24 months) and are guaranteed a unit in the new development with the same or larger area. This is structured into the developer agreement before any work begins." },
  { q: "How long does the entire redevelopment process take?", a: "The consent and approval phase typically takes 4–8 months depending on society size and regulatory complexity. Construction takes 18–30 months depending on project scale. Total timeline from initiation to handover is approximately 24–36 months. We provide a detailed milestone schedule during the feasibility phase." },
];

export default function RedevelopmentPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Redevelopment — Unlock Hidden Value in Old Properties | CashMyProperty.ai",
    description: "Turn old buildings into modern high-value real estate. FSI analysis, society consent management, developer bidding, and complete legal support. Get a brand-new unit 25–40% larger at zero cost. RERA-registered developers with bank guarantees. Full construction monitoring included.",
    canonicalUrl: "https://cashmyproperty.ai/services/redevelopment",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20luxury%20residential%20tower%20rising%20above%20older%20low-rise%20buildings%20in%20Indian%20cityscape%2C%20dramatic%20golden%20hour%20lighting%2C%20construction%20crane%20against%20warm%20amber%20sky%2C%20urban%20redevelopment%20transformation%2C%20cinematic%20architectural%20photography%2C%20deep%20teal%20and%20gold%20palette%2C%20high%20detail%2C%20India%20urban%20landscape&width=1800&height=800&seq=service-redevelopment-hero&orientation=landscape"
          alt="Redevelopment — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Property</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Redevelopment</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Convert old buildings into modern assets. FSI optimization, consent management, and developer bidding — all at zero cost to you.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Redevelopment</span>
          </nav>
        </div>
      </section>

      {/* What is Redevelopment */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What is Redevelopment?</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Old Building. Prime Land. Massive Upside.
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Many older buildings in prime city locations sit on land worth far more than the structure itself. The building may be 30–40 years old, but the land underneath is in a location where new apartments sell for premium prices. That gap between current value and potential value is your opportunity.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Redevelopment allows existing owners to <strong className="text-foreground-950">demolish the old structure and build a modern one</strong> — with zero investment from your side. A developer funds the entire project in exchange for the additional saleable area created by optimizing the FSI. You get a brand-new unit that is typically <strong className="text-foreground-950">25–40% larger</strong> than your original.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Add a corpus fund, rent compensation during construction, modern amenities, and 200–300% property appreciation — and redevelopment becomes the single most profitable decision a property owner can make.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Architectural%20blueprint%20and%203D%20building%20model%20on%20meeting%20table%20with%20professional%20team%20discussion%2C%20modern%20office%20setting%2C%20warm%20amber%20lighting%2C%20real%20estate%20development%20planning%20scene%2C%20clean%20professional%20aesthetic%2C%20India%20corporate%20real%20estate%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20palette&width=800&height=600&seq=redevelopment-meeting-01&orientation=landscape"
                alt="Redevelopment Planning"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">From Old Building to Modern Asset</h2>
            <p className="text-foreground-600">Five stages. Zero cost to owners. A brand-new home at the end.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Is Your Property Right for Redevelopment */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Eligibility</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Is Your Property Right for Redevelopment?</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { check: true, label: "Building is 25+ years old in a prime or developing location" },
              { check: true, label: "Plot area of 500+ sq.m with available FSI headroom" },
              { check: true, label: "Property is a society, apartment complex, or multi-owner building" },
              { check: true, label: "Clear title deeds and no major encumbrances" },
              { check: true, label: "Majority of owners (75%+) open to redevelopment" },
              { check: false, label: "Single-owner standalone house under 200 sq.m plot" },
              { check: false, label: "Heritage or protected buildings with redevelopment restrictions" },
              { check: false, label: "Properties in zones with very low permissible FSI" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl card-dark">
                <i className={`${item.check ? "ri-checkbox-circle-fill text-primary-500" : "ri-close-circle-fill text-foreground-400"} text-lg shrink-0`} />
                <span className={`text-sm ${item.check ? "text-foreground-800" : "text-foreground-400"}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Redevelopment Beats Selling</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "25–40%", label: "Larger New Unit" },
              { value: "200–300%", label: "Value Appreciation" },
              { value: "24–36 Mo", label: "Total Timeline" },
              { value: "₹0", label: "Your Investment" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Is Your Building Sitting on Hidden Value?
          </h2>
          <p className="text-foreground-600 mb-8">
            Get a free FSI assessment and see exactly how much your property could be worth after redevelopment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get FSI Assessment — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}